import {
    AccessLevel,
    GlobalSearchResultType,
    IGlobalSearchResponse,
    IHierarchyChild,
} from '@kcftech/prometheustypescriptapiclient';
import { groupBy, skipLast, sortBy, take, unique } from 'utils/array';
import {
    getCorporationDashboardBaseRoute,
    getCorporationLandingPageRoute,
    getHardwareAdminRoute,
    getHardwareDetailsRoute,
    getLocationDashboardBaseRoute,
    getLocationLandingPageRoute,
    getLocationWorkbenchBaseRoute,
    getSystemWorkbenchDefaultRoute,
    getSystemWorkbenchTab,
    WorkbenchTab,
} from 'utils/RouteHelper';
import { Lookup } from 'utils/types';

export const maxResults = 15;
export const localSearchChar = '/';
export const groupingChar = '"';

const weights: Lookup<GlobalSearchResultType, number> = {
    Corporation: 1000,
    Location: 100,
    System: 100,
    Asset: 10,
    BaseStation: 10,
    Node: 10,
};

export type GlobalSearchIconType =
    | 'overview'
    | 'dashboard'
    | 'workbench'
    | 'baseStation'
    | 'node';

export type ParsedSearchString = Readonly<{
    /**
     * The search string to be sent to the server.
     */
    remoteString: string;
    /**
     * The parsed remote search tokens.
     */
    remoteTokens: string[];
    /**
     * The local search string.
     */
    localString: string | undefined;
    /**
     * The parsed local search tokens.
     */
    localTokens: string[];
}>;

export type GlobalSearchTargetConfig = Readonly<{
    /**
     * The formatted page name.
     */
    targetName: string;
    /**
     * The type of global search result required to generate this target.
     */
    entityType: GlobalSearchResultType;
    /**
     * The type of icon to display.
     */
    iconType: GlobalSearchIconType;
    /**
     * Optional function used to generate the path to a link.
     */
    createPath?: (result: IGlobalSearchResponse) => string[];
    /**
     * Function used to generate a link from a response.
     */
    createUrl: (result: IGlobalSearchResponse) => string;
    /**
     * If the search target requires premium access.
     */
    requiresPremium: boolean;
}>;

export type ResponseAndTargetConfig = Readonly<{
    response: IGlobalSearchResponse;
    targetConfig: GlobalSearchTargetConfig;
}>;

/**
 * Returns the path info from a given hierarchy (skips the last hierarchy child).
 */
export const getPathForHierarchy = (
    hierarchy: Pick<IHierarchyChild, 'HierarchyName'>[] | undefined
): string[] => skipLast(hierarchy ?? [], 1).map(({ HierarchyName }) => HierarchyName);

const locationPath = ({ ParentCorporation }: IGlobalSearchResponse) => [
    ParentCorporation?.ParentCorporationName ?? '[corp missing]',
];

const systemPath = ({ ParentCorporation, ParentLocation }: IGlobalSearchResponse) => [
    ParentCorporation?.ParentCorporationName ?? '[corp missing]',
    ParentLocation?.ParentLocationName ?? '[location missing]',
];

const assetPath = ({
    ParentCorporation,
    ParentLocation,
    GlobalSearchHierarchy,
}: IGlobalSearchResponse) => [
    ParentCorporation?.ParentCorporationName ?? '[corp missing]',
    ParentLocation?.ParentLocationName ?? '[location missing]',
    ...getPathForHierarchy(GlobalSearchHierarchy),
];

const hardwarePath = ({ ParentCorporation, ParentLocation }: IGlobalSearchResponse) => [
    ParentCorporation?.ParentCorporationName ?? '[corp missing]',
    ParentLocation?.ParentLocationName ?? '[location missing]',
];

export const globalSearchTargetConfig: GlobalSearchTargetConfig[] = [
    {
        targetName: 'Corporation Overview',
        entityType: GlobalSearchResultType.Corporation,
        iconType: 'overview',
        createUrl: ({ GlobalSearchId: corporationId }) =>
            getCorporationLandingPageRoute(corporationId),
        requiresPremium: false,
    },
    {
        targetName: 'Corporation Dashboards',
        entityType: GlobalSearchResultType.Corporation,
        iconType: 'dashboard',
        createUrl: ({ GlobalSearchId: corporationId }) =>
            getCorporationDashboardBaseRoute(corporationId),
        requiresPremium: true,
    },
    {
        targetName: 'Location Overview',
        entityType: GlobalSearchResultType.Location,
        iconType: 'overview',
        createPath: locationPath,
        createUrl: ({ GlobalSearchId: locationId }) =>
            getLocationLandingPageRoute(locationId),
        requiresPremium: false,
    },
    {
        targetName: 'Location Dashboards',
        entityType: GlobalSearchResultType.Location,
        iconType: 'dashboard',
        createPath: locationPath,
        createUrl: ({ GlobalSearchId: locationId }) =>
            getLocationDashboardBaseRoute(locationId),
        requiresPremium: true,
    },
    {
        targetName: 'Location Workbench',
        entityType: GlobalSearchResultType.Location,
        iconType: 'workbench',
        createPath: locationPath,
        createUrl: ({ GlobalSearchId: locationId }) =>
            getLocationWorkbenchBaseRoute(locationId),
        requiresPremium: true,
    },
    {
        targetName: 'System Workbench',
        entityType: GlobalSearchResultType.System,
        iconType: 'workbench',
        createPath: systemPath,
        createUrl: ({ GlobalSearchId: systemId }) =>
            getSystemWorkbenchDefaultRoute(systemId),
        requiresPremium: true,
    },
    {
        targetName: 'Asset Workbench',
        entityType: GlobalSearchResultType.Asset,
        iconType: 'workbench',
        createPath: assetPath,
        createUrl: ({ GlobalSearchId: assetId, GlobalSearchHierarchy }) =>
            getSystemWorkbenchTab(
                0 in GlobalSearchHierarchy ? GlobalSearchHierarchy[0].HierarchyId : '',
                WorkbenchTab.Trend,
                assetId
            ),
        requiresPremium: true,
    },
    {
        targetName: 'Base Station',
        entityType: GlobalSearchResultType.BaseStation,
        iconType: 'baseStation',
        createPath: hardwarePath,
        createUrl: ({ GlobalSearchId: baseStationId, ParentLocation }) =>
            ParentLocation?.ParentLocationId
                ? getHardwareDetailsRoute(
                      ParentLocation.ParentLocationId,
                      'baseStations',
                      Number(baseStationId)
                  )
                : // you should only be able to see these results if you have
                  // permissions for the adminSetup pages anyway afaik
                  getHardwareAdminRoute(undefined, 'baseStations', Number(baseStationId)),
        requiresPremium: false,
    },
    {
        targetName: 'Sensor',
        entityType: GlobalSearchResultType.Node,
        iconType: 'node',
        createPath: hardwarePath,
        createUrl: ({ GlobalSearchId: nodeId, ParentLocation }) =>
            ParentLocation?.ParentLocationId
                ? getHardwareDetailsRoute(
                      ParentLocation.ParentLocationId,
                      'sensors',
                      Number(nodeId)
                  )
                : // you should only be able to see these results if you have
                  // permissions for the adminSetup pages anyway afaik
                  getHardwareAdminRoute(undefined, 'sensors', Number(nodeId)),
        requiresPremium: false,
    },
];

/**
 * Search target config grouped by entity type.
 */
export const groupedGlobalSearchTargetConfig = groupBy(
    globalSearchTargetConfig,
    ({ entityType }) => entityType
);

/**
 * Returns a list of unique tokens with a length > 0.
 */
export const normalizeTokens = (tokens: string[]) =>
    unique(tokens.filter((token) => token.length > 0));

/**
 * Splits a string into a list of tokens on spaces not escaped by double quotes.
 */
export const parseTokens = (searchTerm: string): string[] => {
    const { tokens, token } = searchTerm
        .toLowerCase()
        .trim()
        .split('')
        .reduce<{ tokens: string[]; token: string; stringMode: boolean }>(
            (res, c) => {
                switch (c) {
                    case '"':
                        return { ...res, stringMode: !res.stringMode };
                    case ' ':
                        return res.stringMode
                            ? { ...res, token: res.token + c }
                            : { ...res, tokens: [...res.tokens, res.token], token: '' };
                    default:
                        return { ...res, token: res.token + c };
                }
            },
            { tokens: [], token: '', stringMode: false }
        );

    return normalizeTokens(token.length > 0 ? [...tokens, token] : tokens);
};

/**
 * Returns true if the supplied term contains an odd number of `"` characters.
 */
export const isGrouped = (partialTerm: string) =>
    partialTerm.split('').filter((subChar) => subChar === groupingChar).length % 2 !== 0;

/**
 * Parses a search string into a list of remote and local tokens.
 * Remote tokens are all tokens before the first unescaped `/` character.
 * Local tokens are all tokens after the first unescaped `/` character.
 */
export const parseSearchString = (searchString: string): ParsedSearchString => {
    const splitIndex = searchString
        .split('')
        .findIndex(
            (char, index) =>
                char === localSearchChar && !isGrouped(searchString.substr(0, index))
        );

    const remoteString =
        splitIndex > -1 ? searchString.substr(0, splitIndex) : searchString;

    const localString = splitIndex > -1 ? searchString.substr(splitIndex + 1) : undefined;

    return {
        remoteString,
        remoteTokens: parseTokens(remoteString),
        localString,
        localTokens: localString ? parseTokens(localString) : [],
    };
};

/**
 * Calculates the score for the supplied search string, token, and token index (0 based).
 *
 * `token score` = `[length of token] / ([index of token in token list] + 1) / ([position of match] + 1)`
 */
export const calculateScore = (
    searchString: string,
    token: string,
    tokenIndex: number
) => {
    const baseScore = token.length / (tokenIndex + 1);
    const matchIndex = searchString.indexOf(token) + 1;
    return matchIndex > 0 ? baseScore / matchIndex : 0;
};

/**
 * Calculates the score for the supplied search string and tokens.
 *
 * `token score` = `[length of token] / ([index of token in token list] + 1) / ([position of match] + 1)`
 *
 * The sum of all token scores is returned.  If any of the tokens are not found
 * in the `searchString`, returns `0`.
 */
export const calculateTotalScore = (searchString: string, tokens: string[]) => {
    const lower = searchString.toLocaleLowerCase();

    return tokens.reduce((sum, token, tokenIndex) => {
        if (sum === 0 && tokenIndex > 0) {
            return 0;
        }
        const score = calculateScore(lower, token, tokenIndex);
        return score === 0 ? 0 : sum + score;
    }, 0);
};

export function filterResultsAndTargets(
    responsesAndTargets: ResponseAndTargetConfig[],
    localTokens: string[]
): ResponseAndTargetConfig[] {
    if (localTokens.length === 0) {
        return responsesAndTargets;
    }

    // If there are local search tokens, score and filter
    const filtered = responsesAndTargets
        .map((responseAndTarget) => ({
            score: calculateTotalScore(
                responseAndTarget.targetConfig.targetName,
                localTokens
            ),
            responseAndTarget,
        }))
        .filter(({ score }) => score > 0);

    // Reorder results using local score
    return sortBy(filtered, { key: ({ score }) => score, dir: 'desc' }).map(
        ({ responseAndTarget }) => responseAndTarget
    );
}

export function sortAndLimitResultsAndTargets(
    resultsAndTargets: ResponseAndTargetConfig[]
): ResponseAndTargetConfig[] {
    return take(
        sortBy(resultsAndTargets, {
            key: ({ targetConfig: { entityType }, response: { GlobalSearchScore } }) =>
                GlobalSearchScore + weights[entityType],
            dir: 'desc',
        }),
        maxResults
    );
}

export function getFilteredResultsAndTargets(
    responses: IGlobalSearchResponse[],
    localTokens: string[]
): ResponseAndTargetConfig[] {
    if (responses.length === 0) {
        return [];
    }

    const resultsAndTargets = responses.flatMap((response) =>
        (groupedGlobalSearchTargetConfig[response.GlobalSearchResultType] ?? [])
            .filter(
                (targetConfig) =>
                    !targetConfig.requiresPremium ||
                    response.GlobalSearchAccessLevel === AccessLevel.Premium
            )
            .map<ResponseAndTargetConfig>((targetConfig) => ({
                response,
                targetConfig,
            }))
    );

    return filterResultsAndTargets(
        sortAndLimitResultsAndTargets(resultsAndTargets),
        localTokens
    );
}

export type GlobalSearchTargetInfo = Readonly<{
    /**
     * The name of the location to link to.
     */
    targetName: string;
    /**
     * The name of the entity.
     */
    entityName: string;
    /**
     * The parent(s) of the location to link to.
     */
    entityPath: string[];
    /**
     * The URL to navigate to.
     */
    url: string;
    /**
     * The type of entity.
     */
    entityType: GlobalSearchResultType;
    /**
     * The type of icon to display.
     */
    iconType: GlobalSearchIconType;
    /**
     * The id of the entity.
     */
    entityId: string;
    /**
     * The icon URL.
     */
    iconUrl?: string;
}>;

export function getSearchTargets(
    responses: IGlobalSearchResponse[],
    localTokens: string[]
): GlobalSearchTargetInfo[] {
    return sortAndLimitResultsAndTargets(
        getFilteredResultsAndTargets(responses, localTokens)
    ).map<GlobalSearchTargetInfo>(
        ({
            response,
            targetConfig: {
                targetName,
                createPath,
                createUrl: createLink,
                entityType,
                iconType,
            },
        }) => ({
            targetName,
            entityName: response.GlobalSearchName,
            entityPath: createPath?.(response) ?? [],
            url: createLink(response),
            entityType,
            iconType,
            entityId: response.GlobalSearchId,
            iconUrl: response.GlobalSearchIcon,
        })
    );
}
